/**
 * Client-side mirror of server/middleware/planLimits.js PLAN_LIMITS.
 *
 * SINGLE SOURCE OF TRUTH on the client. Whenever you find yourself
 * writing `user?.subscription === 'pro'` STOP — use `hasFeature()`
 * or `getMaxPages()` from this file, and pass the plan from
 * `useEffectivePlan()` / `getEffectivePlan()` (accountStore) so team
 * members inherit the workspace owner's tier. Otherwise Free members
 * inside an Enterprise workspace see locked features, and the next
 * tier rename silently breaks everyone above 'pro' in the ladder.
 *
 * Kept in lockstep with the server. If you change features here,
 * change them in server/middleware/planLimits.js too.
 *
 * Legacy 'plus' tier mapping
 *   Migration 020 maps stored 'plus' rows to 'pro'. We still defend
 *   here so a stale browser cache (user logged in pre-migration)
 *   doesn't accidentally render as Free.
 */

import type { Tier } from './pricing';

export type EffectiveTier = Tier; // 'free' | 'pro' | 'agency' | 'enterprise'

/**
 * Canonical iteration order for tier-aware UIs (filters, dropdowns,
 * stats grids). Matches the pricing card order on the marketing site
 * so users see the same progression everywhere — Free first (entry),
 * then ascending paid tiers.
 */
export const EFFECTIVE_TIERS: readonly EffectiveTier[] = ['free', 'pro', 'agency', 'enterprise'] as const;

export type FeatureKey =
  // Hosting & branding
  | 'customDomain'
  // Conversion infra
  | 'multiplePixels'
  | 'metaCapi'
  | 'gtmAutoSync'
  | 'pixelManager'
  | 'audienceExport'
  | 'affiliatePostback'
  | 'capiReplay' // Enterprise-only: backfill events from a rotated Pixel
  // Anti-bot
  | 'cloaking'
  | 'aiSafePages'
  | 'redirect'
  | 'geoFence'
  | 'botLogs'
  // Analytics
  | 'topCities'
  | 'dwellTime'
  | 'csvExport'
  // Builder
  | 'customCss';

export type WatermarkMode = 'forced' | 'optional' | 'whitelabel';

interface ClientPlanLimits {
  maxPages: number;          // -1 = unlimited
  maxCustomDomains: number;  // -1 = unlimited
  maxFacebookPixels: number; // -1 = unlimited
  maxTeamMembers: number;    // -1 = unlimited, 0 = collaboration disabled
  features: Record<FeatureKey, boolean>;
  brandingWatermark: WatermarkMode;
}

export const PLAN_LIMITS: Record<EffectiveTier, ClientPlanLimits> = {
  free: {
    maxPages: 1,
    maxCustomDomains: 0,
    maxFacebookPixels: 1,
    maxTeamMembers: 0,
    brandingWatermark: 'forced',
    features: {
      customDomain: false,
      multiplePixels: false,
      metaCapi: false,
      gtmAutoSync: false,
      pixelManager: true,
      audienceExport: false,
      affiliatePostback: false,
      capiReplay: false,
      cloaking: false,
      aiSafePages: false,
      redirect: false,
      geoFence: false,
      botLogs: false,
      topCities: false,
      dwellTime: false,
      csvExport: false,
      customCss: false,
    },
  },
  pro: {
    maxPages: 5,
    maxCustomDomains: 3,
    maxFacebookPixels: 3,
    maxTeamMembers: 2,
    brandingWatermark: 'optional',
    features: {
      customDomain: true,
      multiplePixels: false,
      metaCapi: false, // Agency+ — see server PLAN_LIMITS for rationale
      gtmAutoSync: false,
      pixelManager: true,
      audienceExport: false,
      affiliatePostback: true,
      capiReplay: false,
      cloaking: false,
      aiSafePages: false,
      redirect: true,
      geoFence: true,
      botLogs: false,
      topCities: true,
      dwellTime: true,
      csvExport: true,
      customCss: false,
    },
  },
  agency: {
    maxPages: 20,
    maxCustomDomains: 15,
    maxFacebookPixels: -1,
    maxTeamMembers: 5,
    brandingWatermark: 'optional',
    features: {
      customDomain: true,
      multiplePixels: true,
      metaCapi: true,
      gtmAutoSync: false,
      pixelManager: true,
      audienceExport: true,
      affiliatePostback: true,
      capiReplay: false,
      cloaking: true,
      aiSafePages: true,
      redirect: true,
      geoFence: true,
      botLogs: true,
      topCities: true,
      dwellTime: true,
      csvExport: true,
      customCss: true,
    },
  },
  enterprise: {
    maxPages: -1,
    maxCustomDomains: -1,
    maxFacebookPixels: -1,
    maxTeamMembers: -1,
    brandingWatermark: 'whitelabel',
    features: {
      customDomain: true,
      multiplePixels: true,
      metaCapi: true,
      gtmAutoSync: true,
      pixelManager: true,
      audienceExport: true,
      affiliatePostback: true,
      capiReplay: true,
      cloaking: true,
      aiSafePages: true,
      redirect: true,
      geoFence: true,
      botLogs: true,
      topCities: true,
      dwellTime: true,
      csvExport: true,
      customCss: true,
    },
  },
};

/**
 * Resolve a stored subscription string to an effective tier. Maps the
 * legacy 'plus' value to 'pro' and falls back to 'free' for any unknown
 * input — same semantics as server-side normaliseTier().
 */
export function normalizeTier(subscription: string | null | undefined): EffectiveTier {
  if (subscription === 'plus') return 'pro';
  if (subscription === 'pro' || subscription === 'agency' || subscription === 'enterprise' || subscription === 'free') {
    return subscription;
  }
  return 'free';
}

export function hasFeature(subscription: string | null | undefined, feature: FeatureKey): boolean {
  const tier = normalizeTier(subscription);
  return PLAN_LIMITS[tier].features[feature] === true;
}

export function getMaxPages(subscription: string | null | undefined): number {
  return PLAN_LIMITS[normalizeTier(subscription)].maxPages;
}

export function getMaxCustomDomains(subscription: string | null | undefined): number {
  return PLAN_LIMITS[normalizeTier(subscription)].maxCustomDomains;
}

export function getMaxFacebookPixels(subscription: string | null | undefined): number {
  return PLAN_LIMITS[normalizeTier(subscription)].maxFacebookPixels;
}

/** Max team members (seats) the account owner can invite. -1 = unlimited,
 *  0 = the plan doesn't include team collaboration. */
export function getMaxTeamMembers(subscription: string | null | undefined): number {
  return PLAN_LIMITS[normalizeTier(subscription)].maxTeamMembers;
}

/** Whether the plan includes team collaboration at all (seats > 0 or
 *  unlimited). Drives nav visibility + upgrade prompts. */
export function hasTeamFeature(subscription: string | null | undefined): boolean {
  const seats = getMaxTeamMembers(subscription);
  return seats === -1 || seats > 0;
}

export function getWatermarkMode(subscription: string | null | undefined): WatermarkMode {
  return PLAN_LIMITS[normalizeTier(subscription)].brandingWatermark;
}

/** Any paid tier (Pro/Agency/Enterprise) — convenience for boolean checks
 *  that don't care about specific feature flags ("paid customer or not"). */
export function isPaidTier(subscription: string | null | undefined): boolean {
  return normalizeTier(subscription) !== 'free';
}

/** Pretty-print a tier for UI badges ("PRO", "AGENCY", etc.). Always
 *  returns a non-empty string so it's safe to render directly. */
export function tierLabel(subscription: string | null | undefined): string {
  return normalizeTier(subscription).toUpperCase();
}
